/**
 * Self-presence state-machine driver — watches `document.visibilityState`,
 * tracks idle time via user-input events, observes the shell's active
 * channel, and tracks the transition locally.
 *
 * P1.8 C5 alignment: `UpdatePresence` is not on the wire (Q-I8 CLARIFY —
 * the server infers presence from `PlayerSnapshot.connected`). The
 * driver still maintains the local `usePresence` store so the UI has a
 * coherent self-state; it no longer dispatches an intent on transitions.
 *
 * Returns a teardown function — `bootstrapSelfPresence` wires it up
 * at app boot and tears down on page unload / bootstrap close.
 */

import type { Presence } from '../wire';
import { useLiveWire } from '../wire';
import { useShell } from '../shell';
import { usePresence } from './store';

const AWAY_AFTER_MS = 5 * 60 * 1000;
const MIN_TRANSITION_MS = 2_000;

/**
 * Pure state reducer: given current shell/input observables, return the
 * presence state the self-driver should hold right now. Exported for
 * unit testing.
 */
export function resolveSelfPresence(args: {
  connected: boolean;
  documentHidden: boolean;
  worldActive: boolean;
  lastInputAtMs: number;
  nowMs: number;
}): Presence {
  if (!args.connected) return 'Offline';
  if (args.documentHidden) return 'Away';
  if (args.nowMs - args.lastInputAtMs > AWAY_AFTER_MS) return 'Away';
  if (args.worldActive) return 'InWorld';
  return 'Online';
}

export interface SelfPresenceDriver {
  destroy: () => void;
}

export function createSelfPresenceDriver(): SelfPresenceDriver {
  let lastInputAtMs = performance.now();
  let lastTransitionAtMs = 0;

  const evaluate = (): void => {
    const shellState = useShell.getState();
    const wireState = useLiveWire.getState();
    const connected = wireState.ready;
    const documentHidden =
      typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const worldActive = shellState.activeChannel === 'world';
    const nowMs = performance.now();

    const next = resolveSelfPresence({
      connected,
      documentHidden,
      worldActive,
      lastInputAtMs,
      nowMs,
    });

    const { self, selfSince, setSelf } = usePresence.getState();
    if (next === self) return;
    if (nowMs - lastTransitionAtMs < MIN_TRANSITION_MS) return;

    lastTransitionAtMs = nowMs;
    setSelf(next, selfSince + 1n);
  };

  const onInput = (): void => {
    lastInputAtMs = performance.now();
    evaluate();
  };
  const onVisibility = (): void => {
    evaluate();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('pointerdown', onInput, { passive: true });
    window.addEventListener('keydown', onInput, { passive: true });
    window.addEventListener('pointermove', onInput, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
  }

  // Re-evaluate on channel change + wire-readiness change.
  const unsubShell = useShell.subscribe(() => evaluate());
  const unsubWire = useLiveWire.subscribe(() => evaluate());

  // Poll for away-timeout transitions even with no new input.
  const pollId =
    typeof window !== 'undefined'
      ? window.setInterval(evaluate, 30_000)
      : null;

  return {
    destroy: () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('pointerdown', onInput);
        window.removeEventListener('keydown', onInput);
        window.removeEventListener('pointermove', onInput);
        document.removeEventListener('visibilitychange', onVisibility);
      }
      unsubShell();
      unsubWire();
      if (pollId !== null) window.clearInterval(pollId);
    },
  };
}

